import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { MaskedReveal, FadeUp } from "@/components/motion/Reveal";
import { FIRM, PRACTICE_AREAS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", practice_area: "General Enquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        practice_area: form.practice_area,
        message: form.message || "",
        ...(form.email ? { email: form.email } : {}),
      };
      await axios.post(`${API}/consultations`, payload);
      toast.success("Consultation request received. We will confirm within one working day.");
      setSent(true);
      setForm({ name: "", phone: "", email: "", practice_area: "General Enquiry", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page">
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-8">
            <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Chapter 00 · Contact</div>
            <MaskedReveal as="h1" className="serif text-6xl md:text-[9vw] leading-[0.9]" lines={["Speak with", <em className="italic text-gold">counsel.</em>]} />
          </div>
          <FadeUp delay={0.35} className="col-span-12 md:col-span-4 mt-6 md:mt-16 text-[hsl(var(--muted-foreground))]">
            Consultations are confidential. Share as much or as little as you like — a member of our team will respond within one working day.
          </FadeUp>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-10">
          {/* Form */}
          <div className="col-span-12 md:col-span-7">
            <form onSubmit={submit} className="space-y-8" data-testid="consultation-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Field label="Full name" required value={form.name} onChange={setField("name")} testid="c-name" />
                <Field label="Phone" required value={form.phone} onChange={setField("phone")} testid="c-phone" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Field label="Email" type="email" value={form.email} onChange={setField("email")} testid="c-email" />
                <div className="border-b hairline">
                  <label className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))] block mb-2">Practice Area</label>
                  <select
                    value={form.practice_area} onChange={setField("practice_area")}
                    className="w-full bg-transparent py-2 pb-3 text-lg focus:outline-none"
                    data-testid="c-area"
                  >
                    <option>General Enquiry</option>
                    {PRACTICE_AREAS.map(p => <option key={p.slug}>{p.title}</option>)}
                  </select>
                </div>
              </div>
              <div className="border-b hairline">
                <label className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))] block mb-2">Briefly describe your matter</label>
                <textarea
                  value={form.message} onChange={setField("message")}
                  rows={4}
                  className="w-full bg-transparent py-2 pb-3 text-lg focus:outline-none resize-none"
                  placeholder="e.g. Anticipatory bail needed for..."
                  data-testid="c-message"
                />
              </div>

              <div className="flex items-center gap-4 pt-2">
                <button type="submit" disabled={loading} className="btn-ink" data-testid="c-submit">
                  {loading ? "Sending..." : sent ? "Sent · Send another" : "Request Consultation"} <ArrowUpRight className="h-4 w-4" />
                </button>
                <p className="text-xs text-[hsl(var(--muted-foreground))] max-w-xs">Submitting does not create an attorney–client relationship.</p>
              </div>
            </form>
          </div>

          {/* Info */}
          <aside className="col-span-12 md:col-span-5 space-y-8 md:pl-10 md:border-l hairline">
            <InfoBlock icon={<Phone className="h-4 w-4" />} title="Phone">
              <a href={`tel:${FIRM.phoneRaw}`} className="editorial-link" data-testid="contact-phone">{FIRM.phone}</a>
            </InfoBlock>
            <InfoBlock icon={<Mail className="h-4 w-4" />} title="Email">
              <a href={`mailto:${FIRM.email}`} className="editorial-link" data-testid="contact-email">{FIRM.email}</a>
            </InfoBlock>
            <InfoBlock icon={<MapPin className="h-4 w-4" />} title="Chambers">
              <address className="not-italic leading-relaxed">
                {FIRM.address.line1}<br />
                {FIRM.address.line2}<br />
                {FIRM.address.city} – {FIRM.address.pin}
              </address>
            </InfoBlock>
            <InfoBlock icon={<Clock className="h-4 w-4" />} title="Hours">
              <p>{FIRM.hours}</p>
              <p className="text-gold mono text-xs uppercase tracking-[0.28em] mt-1">24×7 bail line for criminal urgencies</p>
            </InfoBlock>
          </aside>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-4">Chapter 01 · Location</div>
          <MaskedReveal as="h2" className="serif text-4xl md:text-5xl leading-tight mb-8" lines={[<>Find us on <em className="italic text-gold">LBS Marg.</em></>]} />
          <div className="aspect-[16/8] overflow-hidden hairline border grayscale contrast-125">
            <iframe
              title="Helkar & Associates location"
              src="https://www.google.com/maps?q=Phoenix+Paragon+Plaza,+LBS+Marg,+Kurla+West,+Mumbai&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <div className="border-b hairline">
      <label className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))] block mb-2">{label}{required && <span className="text-gold"> *</span>}</label>
      <input type={type} value={value} onChange={onChange} required={required} className="w-full bg-transparent py-2 pb-3 text-lg focus:outline-none" data-testid={testid} />
    </div>
  );
}
function InfoBlock({ icon, title, children }) {
  return (
    <FadeUp>
      <div className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))] flex items-center gap-2 mb-2">
        <span className="text-gold">{icon}</span> {title}
      </div>
      <div className="text-lg serif">{children}</div>
    </FadeUp>
  );
}
